/*
 * Pause/play control for autoplaying background videos (WCAG 2.2.2).
 * The video is injected lazily by <viewport-media>, so media events are
 * listened for in the capture phase on the media wrapper.
 * Under prefers-reduced-motion the video is kept paused until the user plays it.
 */
if (!customElements.get('video-overlay-toggle')) {
  class VideoOverlayToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.media = document.getElementById(this.dataset.mediaId);
      if (!this.button || !this.media) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userStarted = false;
      this.onPlay = this.onPlay.bind(this);
      this.update = this.update.bind(this);

      this.media.addEventListener('play', this.onPlay, true);
      this.media.addEventListener('pause', this.update, true);
      this.button.addEventListener('click', this.toggle.bind(this));

      const video = this.getVideo();
      if (video && this.reducedMotion) video.pause();
      this.update();
    }

    disconnectedCallback() {
      if (!this.media) return;
      this.media.removeEventListener('play', this.onPlay, true);
      this.media.removeEventListener('pause', this.update, true);
    }

    getVideo() {
      return this.media.querySelector('video');
    }

    onPlay() {
      const video = this.getVideo();
      if (video && this.reducedMotion && !this.userStarted) video.pause();
      this.update();
    }

    toggle() {
      const video = this.getVideo();
      if (!video) return;
      if (video.paused) {
        this.userStarted = true;
        video.play().catch(() => {});
      } else {
        this.userStarted = false;
        video.pause();
      }
    }

    update() {
      const video = this.getVideo();
      // Before the video is injected it will autoplay, unless reduced motion is on.
      const paused = video ? video.paused : this.reducedMotion;
      this.button.classList.toggle('is-paused', paused);
      this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
    }
  }

  customElements.define('video-overlay-toggle', VideoOverlayToggle);
}
